import React from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { logout } from "@/lib/api";

const NavItem = ({ to, label, index, end }) => (
    <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
            `flex items-baseline gap-3 py-3 border-b border-[#E6E2D6] transition-colors duration-300 font-sans-tight text-sm ${
                isActive
                    ? "text-[#1B1B1B]"
                    : "text-[#1B1B1B]/50 hover:text-[#1B1B1B]"
            }`
        }
    >
        <span className="eyebrow w-6 shrink-0">{index}</span>
        <span>{label}</span>
    </NavLink>
);

export default function AdminLayout() {
    const { user, loading, setUser } = useAuth();
    const nav = useNavigate();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC]">
                <p className="eyebrow text-[#1B1B1B]/60">Loading…</p>
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;
    if (!user.is_admin) return <Navigate to="/admin/denied" replace />;

    const handleLogout = async () => {
        try {
            await logout();
        } catch {}
        setUser(null);
        nav("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#F6F3EC] text-[#1B1B1B]">
            <div className="grid grid-cols-12 min-h-screen">
                {/* Sidebar */}
                <aside className="col-span-12 md:col-span-3 lg:col-span-2 border-r border-[#E6E2D6] flex flex-col p-6 md:p-8">
                    <div className="mb-10">
                        <p className="font-sans-tight text-[13px] tracking-[0.36em] uppercase font-medium">
                            Depmont
                        </p>
                        <p className="eyebrow text-[#1B1B1B]/50 mt-1">
                            Studio Console
                        </p>
                    </div>

                    <nav className="flex-1">
                        <NavItem to="/admin" end index="01" label="Case Studies" />
                        <NavItem
                            to="/admin/careers"
                            index="02"
                            label="Open Positions"
                        />
                        <NavItem to="/admin/invoices" index="03" label="Invoices" />
                        <NavItem
                            to="/admin/site"
                            index="04"
                            label="Site Copy"
                        />
                        <NavItem
                            to="/admin/legal"
                            index="05"
                            label="Privacy & Terms"
                        />
                        <NavItem
                            to="/admin/invoice-settings"
                            index="06"
                            label="Invoice Settings"
                        />
                    </nav>

                    <div className="mt-10 pt-6 border-t border-[#E6E2D6]">
                        <p className="eyebrow text-[#1B1B1B]/60 mb-2">
                            Signed in
                        </p>
                        <p className="font-sans-tight text-sm break-all">
                            {user.email}
                        </p>
                        <button
                            type="button"
                            onClick={handleLogout}
                            data-testid="admin-logout"
                            className="mt-4 eyebrow link-underline text-[#1B1B1B]/70"
                        >
                            Log out ↗
                        </button>
                        <a
                            href="/"
                            className="mt-3 block eyebrow link-underline text-[#1B1B1B]/70"
                        >
                            View site ↗
                        </a>
                    </div>
                </aside>

                {/* Main */}
                <main className="col-span-12 md:col-span-9 lg:col-span-10 p-6 md:p-10 lg:p-14">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
